import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  deleteOrganization,
  deleteUser,
  updateOrganization,
  updateUser,
  userChangePassword,
  withOrganizationUpdateRequired,
  withUserUpdateRequired,
} from "../../api/api";
import { replaceActiveToken } from "../../lib/sessionToken";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import UsernameField from "../../widgets/UsernameField";
import CreatePasswordField from "../../widgets/CreatePasswordField";
import { cn } from "../../lib/utils";
import { ViewIcon, ViewOffIcon } from "../../widgets/icons";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * @param {string} value
 * @returns {boolean}
 */
function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * @param {string} value
 * @returns {boolean}
 */
function isValidEmail(value) {
  return hasText(value) && EMAIL_PATTERN.test(value.trim());
}

/**
 * Password input with show/hide toggle.
 * @param {Object} props
 * @param {string} props.id
 * @param {string} props.label
 * @param {string} props.value
 * @param {function(string): void} props.onChange
 * @param {string} [props.autoComplete]
 * @param {string} [props.error]
 * @returns {JSX.Element}
 */
function PasswordField({ id, label, value, onChange, autoComplete, error }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={Boolean(error)}
          className={cn("pr-11", error && "border-error")}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((prev) => !prev)}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
          aria-label={show ? "Hide password" : "Show password"}
          aria-pressed={show}
        >
          {show ? <ViewOffIcon size={18} /> : <ViewIcon size={18} />}
        </button>
      </div>
      {error ? (
        <p className="text-sm text-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Account identity, password, and deletion.
 * @param {Object} props
 * @param {number} props.userId
 * @param {Object} props.user
 * @param {function(Object): void} props.onAccountUpdated
 * @param {function(): Promise<string>} [props.onAccountRemoved] Where to go after this account is deleted
 * @returns {JSX.Element}
 */
function AccountTab({ userId, user, onAccountUpdated, onAccountRemoved }) {
  const navigate = useNavigate();
  const isOrg = user?.type === "organization";
  const displayName = isOrg
    ? user?.name || user?.username || "this organization"
    : user?.username || "this account";

  const [identity, setIdentity] = useState({
    username: "",
    email: "",
    recovery_email: "",
  });
  const [identityAttempted, setIdentityAttempted] = useState(false);
  const [identitySaving, setIdentitySaving] = useState(false);
  const [identityError, setIdentityError] = useState("");
  const [identitySuccess, setIdentitySuccess] = useState("");
  const [usernameValid, setUsernameValid] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordAttempted, setPasswordAttempted] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordValid, setPasswordValid] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    setIdentity({
      username: user?.username || "",
      email: user?.email || "",
      recovery_email: user?.recovery_email || "",
    });
    setIdentityAttempted(false);
    setIdentityError("");
    setIdentitySuccess("");
  }, [user?.username, user?.email, user?.recovery_email]);

  const identityErrors = {
    email: !hasText(identity.email)
      ? "Email is required."
      : isValidEmail(identity.email)
        ? ""
        : "Please enter a valid email address.",
    recovery_email: !hasText(identity.recovery_email)
      ? ""
      : !isValidEmail(identity.recovery_email)
        ? "Please enter a valid recovery email."
        : identity.recovery_email.trim().toLowerCase() === identity.email.trim().toLowerCase()
          ? "Recovery email must be different from your primary email."
          : "",
  };
  const identityInvalid = Object.values(identityErrors).some(Boolean) || !usernameValid;
  const identityDirty =
    identity.username.trim() !== (user?.username || "") ||
    identity.email.trim() !== (user?.email || "") ||
    identity.recovery_email.trim() !== (user?.recovery_email || "");

  const passwordErrors = {
    current: hasText(currentPassword) ? "" : "Current password is required.",
  };
  const passwordInvalid = Boolean(passwordErrors.current) || !passwordValid;
  const currentPasswordSubmitError =
    passwordError && /current password/i.test(passwordError) ? passwordError : "";
  const otherPasswordError = currentPasswordSubmitError ? "" : passwordError;

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  const handleIdentityChange = (event) => {
    const { name, value } = event.target;
    setIdentity((prev) => ({ ...prev, [name]: value }));
    setIdentitySuccess("");
    setIdentityError("");
  };

  /**
   * @returns {Promise<void>}
   */
  const handleSaveIdentity = async () => {
    setIdentityAttempted(true);
    setIdentityError("");
    setIdentitySuccess("");
    if (identityInvalid) return;

    setIdentitySaving(true);
    const identityPatch = {
      username: identity.username.trim(),
      email: identity.email.trim(),
      recovery_email: identity.recovery_email.trim(),
    };
    const result = isOrg
      ? await updateOrganization(userId, withOrganizationUpdateRequired(user, identityPatch))
      : await updateUser(userId, withUserUpdateRequired(user, identityPatch));
    setIdentitySaving(false);

    if (result.error || result.success === false) {
      setIdentityError(result.error || "Failed to update account");
      return;
    }
    if (result.data && onAccountUpdated) {
      onAccountUpdated(result.data);
    }
    setIdentitySuccess("Account details saved.");
  };

  /**
   * @param {React.FormEvent} event
   * @returns {Promise<void>}
   */
  const handleChangePassword = async (event) => {
    event.preventDefault();
    setPasswordAttempted(true);
    setPasswordError("");
    setPasswordSuccess("");
    if (passwordInvalid) return;

    setPasswordSaving(true);
    const result = await userChangePassword(userId, currentPassword, newPassword);
    setPasswordSaving(false);

    if (result.error || result.success === false) {
      setPasswordError(result.error || "Failed to change password");
      return;
    }
    if (result.token) {
      replaceActiveToken(result.token);
    }
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordAttempted(false);
    setPasswordSuccess("Password changed.");
  };

  /**
   * @returns {Promise<void>}
   */
  const handleDeleteAccount = async () => {
    setDeleteError("");
    setDeleting(true);
    const result = isOrg ? await deleteOrganization(userId) : await deleteUser(userId);
    setDeleting(false);

    if (result.error || result.success === false) {
      setDeleteError(result.error || "Failed to delete account");
      return;
    }
    setDeleteOpen(false);
    const nextPath = (await onAccountRemoved?.()) || "/";
    navigate(nextPath);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Account</h2>
        <p className="mt-1 text-sm text-text-secondary sm:text-base">
          Manage sign-in details, password, and account deletion
        </p>
      </div>

      <Card className="border-ui-border">
        <CardHeader>
          <CardTitle className="text-base text-text-primary">Account details</CardTitle>
          <CardDescription>Username is used to sign in.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <UsernameField
            id="account-username"
            value={identity.username}
            currentUsername={user?.username || ""}
            excludeId={userId}
            excludeType={isOrg ? "organization" : "user"}
            onChange={(username) => {
              setUsernameValid(false);
              setIdentity((prev) => ({ ...prev, username }));
              setIdentitySuccess("");
              setIdentityError("");
            }}
            onValidityChange={setUsernameValid}
          />
          <div className="space-y-2">
            <Label htmlFor="account-email">Email</Label>
            <Input
              id="account-email"
              name="email"
              type="email"
              autoComplete="email"
              value={identity.email}
              onChange={handleIdentityChange}
              aria-invalid={identityAttempted && Boolean(identityErrors.email)}
            />
            {identityAttempted && identityErrors.email ? (
              <p className="text-sm text-error" role="alert">
                {identityErrors.email}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="account-recovery-email">Recovery email</Label>
            <Input
              id="account-recovery-email"
              name="recovery_email"
              type="email"
              autoComplete="off"
              value={identity.recovery_email}
              onChange={handleIdentityChange}
              aria-invalid={identityAttempted && Boolean(identityErrors.recovery_email)}
            />
            <p className="text-sm text-text-secondary">
              Optional. Used if you lose access to your primary email.
            </p>
            {identityAttempted && identityErrors.recovery_email ? (
              <p className="text-sm text-error" role="alert">
                {identityErrors.recovery_email}
              </p>
            ) : null}
          </div>
          {identityError ? (
            <p className="text-sm text-error" role="alert">
              {identityError}
            </p>
          ) : null}
          {identitySuccess ? (
            <p className="text-sm text-text-secondary" role="status">
              {identitySuccess}
            </p>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end">
          <Button
            type="button"
            onClick={handleSaveIdentity}
            disabled={identitySaving || !identityDirty || identityInvalid}
          >
            {identitySaving ? "Saving…" : "Save details"}
          </Button>
        </CardFooter>
      </Card>

      <Card className="border-ui-border">
        <CardHeader>
          <CardTitle className="text-base text-text-primary">Password</CardTitle>
          <CardDescription>Enter your current password to set a new one</CardDescription>
        </CardHeader>
        <form onSubmit={handleChangePassword}>
          <CardContent className="space-y-4">
            <PasswordField
              id="account-current-password"
              label="Current password"
              value={currentPassword}
              onChange={(value) => {
                setCurrentPassword(value);
                setPasswordError("");
                setPasswordSuccess("");
              }}
              autoComplete="current-password"
              error={
                (passwordAttempted ? passwordErrors.current : "") || currentPasswordSubmitError
              }
            />
            <CreatePasswordField
              id="account-new-password"
              label="New password"
              value={newPassword}
              onChange={(value) => {
                setNewPassword(value);
                setPasswordError("");
                setPasswordSuccess("");
              }}
              showConfirm
              confirmValue={confirmPassword}
              onConfirmChange={(value) => {
                setConfirmPassword(value);
                setPasswordError("");
                setPasswordSuccess("");
              }}
              onValidityChange={setPasswordValid}
            />
            {otherPasswordError ? (
              <p className="text-sm text-error" role="alert">
                {otherPasswordError}
              </p>
            ) : null}
            {passwordSuccess ? (
              <p className="text-sm text-text-secondary" role="status">
                {passwordSuccess}
              </p>
            ) : null}
          </CardContent>
          <CardFooter className="justify-end">
            <Button type="submit" disabled={passwordSaving || passwordInvalid}>
              {passwordSaving ? "Changing…" : "Change password"}
            </Button>
          </CardFooter>
        </form>
      </Card>

      <Card className="border-error">
        <CardHeader>
          <CardTitle className="text-base text-error">Danger zone</CardTitle>
          <CardDescription>
            Permanently delete {isOrg ? "this organization" : "your account"} and its data. This cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardFooter className="justify-end">
          <Button type="button" variant="destructive" onClick={() => setDeleteOpen(true)}>
            Delete account
          </Button>
        </CardFooter>
      </Card>

      <Dialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open && !deleting) {
            setDeleteOpen(false);
            setDeleteError("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {isOrg ? "organization" : "account"}?</DialogTitle>
            <DialogDescription>
              This permanently deletes {displayName} and related data. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError ? (
            <p className="px-6 text-sm text-error" role="alert">
              {deleteError}
            </p>
          ) : null}
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={deleting}
              onClick={() => {
                setDeleteOpen(false);
                setDeleteError("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleting}
              onClick={handleDeleteAccount}
            >
              {deleting ? "Deleting…" : "Delete account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AccountTab;
